<template>
  <div id="attribution-study-edit-modal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-body">
          <div class="d-flex align-items-center p-3">
            <div class="centered-circle flex-shrink-0 bg-secondary text-primary h-10 w-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="feather feather-code small-icon"
              >
                <polyline points="16 18 22 12 16 6"></polyline>
                <polyline points="8 6 2 12 8 18"></polyline>
              </svg>
            </div>
            <div class="ms-3">
              <h3 class="fs-5 spacing-close">Edit Attribution Study</h3>
            </div>
          </div>

          <!-- Content Here -->
          <form>
            <div class="mb-3">
              <label for="deliveryDate" class="form-label">Delivery Date</label>
              <input
                v-model="deliveryDate"
                type="date"
                class="form-control"
                id="deliveryDate"
              />
            </div>

            <div class="mb-3">
              <label for="notes" class="form-label">Notes</label>
              <textarea
                v-model="notes"
                class="form-control"
                id="notes"
              ></textarea>
            </div>

            <div v-if="showRetargetingCsvColumns" class="mb-3">
              <label class="form-label">Export columns</label>
              <div class="form-text mb-2">
                Choose the fields included in each row of the Accretive export file. Selecting any field beyond Device Hash
                produces one row per exposure event (a device can appear multiple times).
              </div>
              <div class="retargeting-csv-columns-list border rounded p-3">
                <div
                  class="form-check"
                  v-for="column in retargetingCsvColumnOptions"
                  :key="column.value"
                >
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="`editRetargetingCsvColumn-${column.value}`"
                    :value="column.value"
                    :checked="isRetargetingCsvColumnSelected(column.value)"
                    :disabled="column.locked"
                    @change="onRetargetingCsvColumnChange(column, $event.target.checked)"
                  />
                  <label class="form-check-label" :for="`editRetargetingCsvColumn-${column.value}`">
                    {{ column.label }}
                    <span v-if="column.locked" class="text-muted">(required)</span>
                  </label>
                  <div v-if="column.note" class="form-text mt-0">
                    {{ column.note }}
                  </div>
                </div>
              </div>
              <div v-if="hasEventLevelRetargetingColumns" class="form-text text-warning mt-2">
                Additional fields create a larger event-level export where device hashes can repeat.
              </div>
            </div>

            <p>Custom Cuts</p>
            <div v-for="(cut, index) in cuts" :key="index" class="mb-3">
              <div class="d-flex align-items-center">
                <div class="me-2 flex-grow-1">
                  <select class="form-select" v-model="cut.cutOrderOne">
                    <option value="" disabled selected>Select Cut 1</option>
                    <option v-for="cutOption in cutOptions" :value="cutOption">
                      {{ cutOption }}
                    </option>
                  </select>
                </div>
                <div class="me-2 flex-grow-1">
                  <select class="form-select" v-model="cut.cutOrderTwo">
                    <option value="" disabled selected>Select Cut 2</option>
                    <option value=""></option>
                    <option v-for="cutOption in cutOptions" :value="cutOption">
                      {{ cutOption }}
                    </option>
                  </select>
                </div>
                <button
                  type="button"
                  class="btn btn-danger btn-sm"
                  @click="removeCut(index)"
                >
                  <font-awesome-icon :icon="['fas', 'minus']" />
                </button>
              </div>
            </div>

            <button type="button" class="btn btn-secondary" @click="addCut()">
              <font-awesome-icon :icon="['fas', 'plus']" class="pe-1" /> Add
              Custom Cut
            </button>
          </form>
          <!-- end content -->
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary font-sm"
            style="min-width: 150px"
            @click="onOkClick"
          >
            Ok
          </button>
          <button
            type="button"
            class="btn btn-outline-primary font-sm"
            data-bs-dismiss="modal"
            style="min-width: 150px"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as dayjs from "dayjs";
import {
  DEVICE_HASH_COLUMN,
  RETARGETING_CSV_COLUMN_OPTIONS,
  getRetargetingCsvColumnsInOrder,
} from "@/data/retargetingCsvColumns";

export default {
  name: "AttributionStudyEditModal",
  props: {
    yesEvent: Function,
    study: Object,
    cuts: {
      type: Array,
      default: () => [{ cutOrderOne: "", cutOrderTwo: "" }],
    },
    cutOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      model: streetmetrics.model,
      currentModal: null,
      modalElement: null,
      errorMessage: null,
      notes: "",
      deliveryDate: "",
      selectedRetargetingCsvColumns: [DEVICE_HASH_COLUMN],
      retargetingCsvColumnOptions: RETARGETING_CSV_COLUMN_OPTIONS,
    };
  },
  computed: {
    showRetargetingCsvColumns() {
      return this.isRetargetingStudy(this.study) && this.isAccretiveStudy(this.study);
    },
    selectedRetargetingCsvExportColumns() {
      return getRetargetingCsvColumnsInOrder(
        this.selectedRetargetingCsvColumns,
        this.showRetargetingCsvColumns
      );
    },
    hasEventLevelRetargetingColumns() {
      return this.selectedRetargetingCsvExportColumns.some(column => column !== DEVICE_HASH_COLUMN);
    },
  },
  mounted() {
    this.modalElement = document.getElementById(
      "attribution-study-edit-modal"
    );
    this.currentModal = new bootstrap.Modal(this.modalElement);

    this.modalElement.addEventListener("hidden.bs.modal", () => {
      this.$emit("noEvent");
    });

    this.currentModal.show();
  },
  beforeDestroy() {
    this.modalElement.removeEventListener("hidden.bs.modal");
  },
  watch: {
    study: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          return;
        }

        this.deliveryDate = dayjs(newValue.deliveryDate * 1000).format(
          "YYYY-MM-DD"
        );
        this.notes = newValue.notes;
        this.selectedRetargetingCsvColumns = this.getStudyRetargetingCsvColumns(newValue);
      },
    },
  },
  methods: {
    addCut() {
      this.cuts.push({ cutOrderOne: "", cutOrderTwo: "" });
    },
    removeCut(index) {
      this.cuts.splice(index, 1);
    },
    onSaveClick() {
      this.errorMessage = null;
    },

    isRetargetingCsvColumnSelected(columnValue) {
      return this.selectedRetargetingCsvExportColumns.includes(columnValue);
    },

    onRetargetingCsvColumnChange(column, checked) {
      if (column.locked) {
        this.ensureDefaultRetargetingCsvColumns();
        return;
      }

      if (checked) {
        if (!this.selectedRetargetingCsvColumns.includes(column.value)) {
          this.selectedRetargetingCsvColumns.push(column.value);
        }
      } else {
        this.selectedRetargetingCsvColumns = this.selectedRetargetingCsvColumns.filter(
          selectedColumn => selectedColumn !== column.value
        );
      }

      this.ensureDefaultRetargetingCsvColumns();
    },

    ensureDefaultRetargetingCsvColumns() {
      if (!this.selectedRetargetingCsvColumns.includes(DEVICE_HASH_COLUMN)) {
        this.selectedRetargetingCsvColumns.unshift(DEVICE_HASH_COLUMN);
      }
    },

    getStudyRetargetingCsvColumns(study) {
      const metadata = this.getStudyMetadata(study);
      return getRetargetingCsvColumnsInOrder(
        study.retargetingCsvColumns || metadata.retargetingCsvColumns || metadata.RETARGETING_CSV_COLUMNS,
        true
      );
    },

    getStudyMetadata(study) {
      const metadata = study.metadata || study.METADATA || {};

      if (typeof metadata === "string") {
        try {
          return JSON.parse(metadata);
        } catch (error) {
          return {};
        }
      }

      return metadata;
    },

    isRetargetingStudy(study) {
      if (!study) {
        return false;
      }

      return this.normalizeIdentifier(study.studyType || study.analysisType) === "RETARGETING";
    },

    isAccretiveStudy(study) {
      if (!study) {
        return false;
      }

      const partnerValues = [
        study.dataPartner,
        study.dataPartnerName,
        study.partner,
        study.partnerName,
        study.DATA_PARTNER,
        study.dataPartnerId,
        study.dataPartnerID,
        study.partnerId,
        study.partnerID,
        study.DATA_PARTNER_ID,
      ];

      return partnerValues.some(partner => this.isAccretivePartnerValue(partner));
    },

    isAccretivePartnerValue(partner) {
      if (!partner) {
        return false;
      }

      if (typeof partner === "number") {
        return partner === 6;
      }

      if (typeof partner === "object") {
        return [
          partner.name,
          partner.id,
          partner.value,
          partner.dataPartner,
          partner.dataPartnerId,
          partner.partnerId,
        ].some(partnerValue => this.isAccretivePartnerValue(partnerValue));
      }

      const normalizedPartner = this.normalizeIdentifier(partner);
      return normalizedPartner === "ACCRETIVE" || normalizedPartner === "6";
    },

    normalizeIdentifier(value) {
      return String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    },

    getRetargetingCsvColumnsPayload() {
      if (!this.showRetargetingCsvColumns) {
        return undefined;
      }

      return this.selectedRetargetingCsvExportColumns;
    },

    noHandler(e) {
      // hide the modal
      this.currentModal.hide();
    },
    onOkClick() {
      // call the parent's "yesEvent"
      if (this.yesEvent) {
        const formattedDate = dayjs(this.deliveryDate).unix();
        this.yesEvent(
          this.study,
          formattedDate,
          this.notes,
          this.cuts,
          this.getRetargetingCsvColumnsPayload()
        );
      }
      // hide the modal
      this.noHandler();
    },
  },
};
</script>

<style></style>
